import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Disc3, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, apiError } from "@/lib/api";
import { brandVars, keepPromo, promoSrc, useMeta } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";


const BANDS = [
  ["TRACK", "bg-sec-track"],
  ["RELEASE", "bg-sec-release"],
  ["CODES", "bg-sec-codes"],
  ["PUBLISHING", "bg-sec-pub"],
  ["MECHANICAL", "bg-sec-mech"],
  ["PRO", "bg-sec-pro"],
  ["DISTRIBUTION", "bg-sec-dist"],
];

/** The app icon: the uploaded one, or the standard disc on the brand colour. */
export function AppIcon({ look, className, size = 40, light }) {
  if (look?.icon_v) return <img src={look.icon_url} alt="" width={size} height={size} className={cn("rounded-xl object-cover shrink-0", className)} />;
  return (
    <div className={cn("rounded-xl flex items-center justify-center shrink-0", light ? "bg-white/10" : "bg-brand", className)} style={{ width: size, height: size }}>
      <Disc3 className="text-white" style={{ width: size * 0.48, height: size * 0.48 }} />
    </div>
  );
}

function PromoButton({ look }) {
  if (!(look?.cta_on && look?.cta_text && look?.cta_url)) return null;
  return (
    <a href={look.cta_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-white text-ink font-semibold text-[13.5px] px-4 py-2 hover:bg-white/90">
      {look.cta_text} <ArrowRight className="w-4 h-4" />
    </a>
  );
}

export function AuthShell({ children, heading, sub, footer, look: lookProp }) {
  const fetched = useMeta();
  const look = lookProp || fetched;
  const name = look.platform_name || "MetaTrack";
  const tagline = look.tagline || "Every song, split, code and registration — in one place.";
  const blurb = look.blurb || "Your catalog, the writers on it and where each work is registered. The app spots missing codes and splits that don't add up for you.";
  const mode = look.login_mode_now || look.login_mode || "standard";
  const promo = mode !== "standard" && look.promo_url ? promoSrc(look) : "";
  const showText = !promo || mode === "promo";
  // before the saved look has arrived (first visit on this device) show only the colour — no default text to swap out
  const ready = look.loaded !== false;
  useEffect(() => {
    if (look.promo_v && !look.stale) keepPromo(look);
  }, [look]);
  // the device's own copy draws at once; a download shows nothing over the panel until it's in
  const [loadedSrc, setLoadedSrc] = useState("");
  const imgIn = !promo || promo.startsWith("data:") || loadedSrc === promo;
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-surface" style={look.brand_color ? brandVars(look.brand_color) : undefined}>
      <div className="hidden lg:flex flex-col justify-between bg-brand text-white p-12 relative overflow-hidden" data-testid="signin-panel">
        {promo && (
          <>
            <img src={promo} alt="" fetchPriority="high" decoding="sync" onLoad={() => setLoadedSrc(promo)} className={cn("absolute inset-0 w-full h-full object-cover", !imgIn && "invisible")} />
            {mode === "promo" && <div className="absolute inset-0 bg-gradient-to-b from-black/25 to-black/60" />}
          </>
        )}
        <div className={cn("relative flex items-center gap-3", ready ? "" : "invisible")}>
          <AppIcon look={look} size={40} light />
          <div className="font-display text-[26px] font-semibold tracking-tight drop-shadow-sm">{name}</div>
        </div>
        <div className={cn("relative max-w-md", ready && imgIn ? "" : "invisible")}>
          {ready && showText && (
            <>
              <p className="font-display text-[34px] leading-[1.15] font-semibold tracking-[-0.02em]">{tagline}</p>
              <p className="mt-4 text-white/75 text-[15px] leading-relaxed">{blurb}</p>
            </>
          )}
          <PromoButton look={look} />
          {ready && !promo && (
            <div className="mt-10 space-y-2">
              {BANDS.map(([label, cls], i) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="font-mono text-[10.5px] tracking-[0.16em] text-white/60 w-[104px]">{label}</span>
                  <span className={`h-1.5 rounded-full ${cls} opacity-90`} style={{ width: `${40 + ((i * 37) % 55)}%` }} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="relative font-mono text-[11px] text-white/40">{promo ? "" : "Catalog · writers · registrations"}</div>
      </div>
      <div className="flex flex-col items-center justify-center p-6 sm:p-10">
        {ready && promo && (
          <div className="lg:hidden relative w-full max-w-[420px] rounded-2xl overflow-hidden mb-7 min-h-[170px] flex flex-col justify-end p-5 text-white bg-brand" data-testid="signin-banner">
            <img src={promo} alt="" fetchPriority="high" decoding="sync" onLoad={() => setLoadedSrc(promo)} className={cn("absolute inset-0 w-full h-full object-cover", !imgIn && "invisible")} />
            {mode === "promo" && <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/65" />}
            <div className={cn("relative", !imgIn && "invisible")}>
              {mode === "promo" && <p className="font-display text-[20px] leading-tight font-semibold">{tagline}</p>}
              <PromoButton look={look} />
            </div>
          </div>
        )}
        <div className="w-full max-w-[380px] animate-rise">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <AppIcon look={look} size={36} />
            <div className="font-display text-[22px] font-semibold text-ink">{name}</div>
          </div>
          <h1 className="font-display text-[26px] font-semibold tracking-[-0.01em] text-ink">{heading}</h1>
          <p className="text-[13.5px] text-muted2 mt-1.5">{sub}</p>
          <div className="mt-7">{children}</div>
          {footer}
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, type, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {type === "password" ? <PasswordInput as={Input} id={id} {...props} /> : <Input id={id} type={type} {...props} />}
    </div>
  );
}

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const meta = useMeta();
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    try {
      const r = window.sessionStorage.getItem("signed_out_reason");
      if (r) {
        setReason(r);
        window.sessionStorage.removeItem("signed_out_reason");
      }
    } catch {
      /* private browsing */
    }
  }, []);

  if (user) return <Navigate to={user.role === "owner" ? "/platform" : "/overview"} replace />;
  const setup = meta.needs_setup;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = setup
        ? await api.post("/auth/register", form)
        : await api.post("/auth/login", { email: form.email, password: form.password });
      setUser(data.user);
      navigate(data.user.role === "owner" ? "/platform" : "/overview");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      look={meta}
      heading={setup ? "Set up your catalog" : "Sign in"}
      sub={setup ? "Create the platform owner account and your first workspace (your publishing company). You can import your spreadsheet next." : "Pick up where you left off."}
    >
      {reason && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-gold-light px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-gold leading-relaxed">{reason}</p>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        {setup && (
          <>
            <Field id="name" label="Your name" required value={form.name} onChange={set("name")} autoComplete="name" />
            <Field id="company" label="Publishing company" placeholder="e.g. Mediaheart Publishing" value={form.company} onChange={set("company")} />
          </>
        )}
        <Field id="email" label="Email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} />
        <Field
          id="password"
          label={setup ? "Choose a password (8+ characters)" : "Password"}
          type="password"
          required
          minLength={setup ? 8 : undefined}
          autoComplete={setup ? "new-password" : "current-password"}
          value={form.password}
          onChange={set("password")}
        />
        <Button type="submit" disabled={busy || !meta.loaded} className="w-full bg-brand hover:bg-brand-hover h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : setup ? "Create account" : "Sign in"}
        </Button>
        {!setup && (
          <p className="text-[12.5px] text-muted2">
            Forgotten your password? Ask your admin for a reset link from the Team page.
          </p>
        )}
      </form>
    </AuthShell>
  );
}

export function SetPassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  if (user === false) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("The two new passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      setUser({ ...user, must_change_password: false });
      toast.success("Password updated");
      navigate(user.role === "owner" ? "/platform" : "/overview");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <AuthShell heading="Choose your own password" sub="You signed in with a password someone else set. Pick a new one to carry on.">
      <form onSubmit={submit} className="space-y-4">
        <Field id="cur" label="Password you were given" type="password" required value={form.current_password} onChange={set("current_password")} />
        <Field id="new" label="New password (8+ characters)" type="password" minLength={8} required value={form.new_password} onChange={set("new_password")} />
        <Field id="conf" label="New password again" type="password" required value={form.confirm} onChange={set("confirm")} />
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save and continue"}
        </Button>
        <button type="button" onClick={logout} className="text-[12.5px] text-muted2 hover:text-ink">
          Sign out instead
        </button>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", new_password: pw });
      toast.success("Password changed — sign in with it now");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell heading="Choose a new password" sub="This link works once and expires after an hour.">
      <form onSubmit={submit} className="space-y-4">
        <Field id="pw" label="New password (8+ characters)" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} />
        <Button type="submit" disabled={busy} className="w-full bg-brand hover:bg-brand-hover h-11">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save password"}
        </Button>
        <Link to="/login" className="link-quiet text-[12.5px]">
          Back to sign in
        </Link>
      </form>
    </AuthShell>
  );
}
